import classNames from 'classnames';
import React, { Component, useEffect } from 'react';

import { action, computed } from 'mobx';
import { observer } from 'mobx-react';

import { isBoolean } from 'lodash';

import { faAngleDown, faAngleUp } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import { useButton } from '@react-aria/button';
import { useFocusRing } from '@react-aria/focus';
import { HiddenSelect, useSelect } from '@react-aria/select';
import { mergeProps } from '@react-aria/utils';

import { Item } from '@react-stately/collections';
import { useSelectState } from '@react-stately/select';

import type { AriaSelectProps } from '@react-types/select';

import useChangeEffect from 'lib/hooks/useChangeEffect';
import ListBox from '../../../listbox';
import { Popover } from '../../../popover';

export interface IProps extends Omit<AriaSelectProps<any>, 'children'> {
  disabled?: boolean;
  displayMember?: string;
  errorText?: string;
  items: Array<IOption | any>;
  label?: string;
  labelClass?: string;
  required?: boolean;
  selectClass?: string;
  submitted?: boolean;
  value?: string | number;
  valueMember?: string;
  nullText?: string;

  onChange: (selectedItem: string | number | boolean) => void;
}

export interface IOption {
  id: number | string;
  name: string;
  value: any;
}

export function toIIOptions(items: string[]): IOption[] {
  return items.map((item) => {
    return { id: item, name: item, value: item };
  });
}

const SelectAria: React.FC<IProps> = observer(({ selectClass = 'click-input', ...props }) => {
  const state = useSelectState(props);
  const ref = React.useRef<HTMLButtonElement>(null);
  const { labelProps, triggerProps, valueProps, menuProps } = useSelect(props, state, ref);
  const { buttonProps } = useButton(triggerProps as any, ref);
  const { focusProps } = useFocusRing();

  const { selectedKey, selectedItem } = state;

  const error = React.useMemo(() => {
    return props.errorText?.split(',')?.at(-1);
  }, [props.errorText]);

  useChangeEffect(() => {
    const value = selectedItem?.props?.childItems[0].value;
    if (BzNotNull(value)) {
      props.onChange(value);
    }
  }, [selectedKey]);

  useEffect(() => {
    const { value, items, valueMember } = props;
    if (!items) {
      return;
    }

    const member = valueMember ?? 'value';
    const lValue = String(value);
    const selectedItem = items.find((item) => String(item[member]) == lValue);

    if (!!selectedItem) {
      state.setSelectedKey(String(selectedItem.id));
    }
  }, [props.value]);

  useEffect(() => {
    // handles items changing size
    const { value, items, valueMember } = props;
    const member = valueMember ?? 'value';
    const lValue = String(value);
    const selectedItem = items.find((item) => String(item[member]) == lValue);

    if (!!selectedItem) {
      state.setSelectedKey(String(selectedItem.id));
    }
  }, props.items);

  return (
    <div className="relative inline-flex w-full flex-col">
      <div {...labelProps} className={classNames('click-label', props.labelClass)}>
        <span>
          {props.label}
          {props.required && <span className="text-normal pl-px text-rose-600">*</span>}
        </span>
      </div>
      <HiddenSelect state={state} triggerRef={ref} label={props.label} name={props.name} />
      <button //currently needed for jest tests
        {...mergeProps(buttonProps, focusProps)}
        ref={ref}
        className={`
          click-input relative flex max-w-prose items-center justify-between rounded-md outline-none
          ${props.disabled ? 'bg-gray-100' : 'bg-white'}
          ${
            props.disabled || props.disabled === undefined
              ? 'cursor-default bg-gray-100'
              : 'cursor-pointer'
          }
          ${!!props.errorText && props.submitted ? 'mb-0' : ''}
          ${
            state.isOpen && !props.disabled
              ? '!border-[3px]  border-clickBlue-dark !px-0 !py-[5px]'
              : `${selectClass} !border-2`
          }
        `}
      >
        <span
          {...valueProps}
          className={`overflow-hidden text-ellipsis whitespace-nowrap pl-3 text-left text-base ${
            state.selectedItem ? 'text-gray-800' : 'text-gray-500'
          }`}
        >
          {state.selectedItem ? state.selectedItem.rendered : props.nullText ?? 'Select an option'}
        </span>
        <span className="px-3">
          <FontAwesomeIcon icon={state.isOpen && !props.disabled ? faAngleUp : faAngleDown} />
        </span>
        {state.isOpen && !props.disabled && (
          <Popover
            isOpen={state.isOpen}
            onClose={state.close}
            className="absolute top-full z-10 mt-2 w-full rounded-md border border-gray-300 bg-white shadow-lg"
          >
            <ListBox {...menuProps} state={state} />
          </Popover>
        )}
      </button>
      {!!props.errorText && props.submitted && (
        <div className="pb-2 text-sm font-medium text-rose-600">
          <p>{error}</p>
        </div>
      )}
    </div>
  );
});

SelectAria.displayName = 'SelectAria';

@observer
class FieldSelect extends Component<IProps> {
  render() {
    return (
      <SelectAria {...this.props} onChange={this.onChange}>
        {this.mappedItems.length > 0 ? (
          this.mappedItems.map((option: IOption, i) => {
            const { id, name } = option;
            return (
              <Item key={!id ? i : id} childItems={[option]}>
                {name}
              </Item>
            );
          })
        ) : (
          <Item>No option here</Item>
        )}
      </SelectAria>
    );
  }

  @computed
  private get intermediateItems(): IOption[] {
    const { items, displayMember, valueMember } = this.props;

    return items.map((item) => {
      let value;
      if (!!valueMember) {
        value = item[valueMember];
      } else {
        value = item?.value;
      }

      let name;
      if (!!displayMember) {
        name = item[displayMember] || value;
      } else {
        name = item?.value || value;
      }

      return { ...item, name, value };
    });
  }

  @computed
  private get mappedItems(): IOption[] {
    return this.intermediateItems.map((item) => {
      return {
        ...item,
        value: isBoolean(item.value) ? String(item.value) : item.value
      };
    });
  }

  @action.bound
  private onChange(value: string | number | boolean): void {
    const { value: newValue } =
      this.intermediateItems.find((ii) => ii.value === value) ??
      this.intermediateItems.find((ii) => String(ii.value) === value)!;

    this.props.onChange(newValue);
  }
}

export default FieldSelect;
